<template>
  <div class="activity-rates-editor">
    <div class="well" v-if="!canLoadRates">
      {{ _('Save the product first to edit the prices of the activity.') }}
    </div>
    <PricingCalendarShell
      v-else
      ref="shell"
      v-model:view="view"
      v-model:month="month"
      v-model:rangeFrom="rangeFrom"
      v-model:rangeTo="rangeTo"
      v-model:rangeDays="rangeDays"
      :views="views"
      fieldPrefix="activityrates"
      :cellClass="dayClass"
      v-on:opening="dialogOpening"
      v-on:cancel="pendingStart = null"
    >
      <template #picker>
        <RateplanPicker
          inputId="activity-rate-plan"
          :plans="plans"
          :selectedId="selectedPlan ? selectedPlan.id : null"
          :optionLabel="planOptionLabel"
          :summary="selectedPlanSummary"
          :help="_('Rates let customers choose between alternatives such as a flexible and a non-refundable price. A rate can follow the prices of another rate with a difference. Rates and prices are saved immediately.')"
          v-on:select="selectPlanById"
          v-on:set-default="selectedPlan && setDefault(selectedPlan)"
          v-on:edit="selectedPlan && editPlan(selectedPlan)"
          v-on:add="addPlan"
        />
        <div class="form-group ar-duration-field" v-if="durations.length > 1">
          <label for="activity-duration">{{ _('Duration') }}</label>
          <select id="activity-duration" class="form-control" :value="String(selectedDuration)" v-on:change="selectDuration($event.target.value)">
            <option v-for="minutes in durations" :key="'dur-' + minutes" :value="String(minutes)">{{ formatDuration(minutes) }}</option>
          </select>
        </div>
        <i class="fa fa-fw fa-cog fa-spin text-muted" v-if="loading"></i>
      </template>

      <template #notice>
        <div class="alert alert-warning" v-if="!startOptions.length">
          <i class="fa fa-fw fa-exclamation-triangle"></i> {{ _('This activity has no start times. Add start times at fixed times, for example 10.00, to price them.') }}
        </div>
        <p class="help-block derived-plan-hint" v-if="isSelectedDerived">{{ derivedPlanHint }}</p>
        <p class="text-success applied-hint" v-if="appliedHint">{{ appliedHint }}</p>
      </template>

      <template #weekly>
        <p class="help-block">{{ _('Click a start time to set its price, or drag across several to change them at once. "All times" and "Every day" fill in where nothing more specific is set.') }}</p>
        <div class="table-responsive">
          <table class="table table-bordered table-condensed ar-week-grid">
            <thead>
              <tr>
                <th
                  v-for="(col, ci) in weekColumns"
                  :key="'wc-' + col.weekday"
                  :class="'ar-week-head ' + weekColumnClass(col.weekday)"
                  :title="_('Select the whole day')"
                  v-on:pointerdown="weekPointerDown(ci, null, $event)"
                  v-on:pointerenter="weekPointerEnter(ci, null)"
                >{{ col.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  v-for="(col, ci) in weekColumns"
                  :key="'wd-' + col.weekday"
                  :class="'ar-week-day ' + weekColumnClass(col.weekday)"
                  v-on:pointerdown="weekPointerDown(ci, null, $event)"
                  v-on:pointerenter="weekPointerEnter(ci, null)"
                >
                  <div class="ar-week-label">{{ col.label }}</div>
                  <div class="ar-week-slots">
                  <div
                    v-for="(row, ri) in weekRows"
                    :key="'ws-' + col.weekday + '-' + row.minute"
                    :class="'ac-slot ac-clickable ' + weekSlotClass(col.weekday, row.minute) + (isWeekCellSelected(ci, ri) ? ' ac-selected' : '')"
                    :title="weekCellTitle(col.weekday, row.minute)"
                    tabindex="0"
                    v-on:pointerdown.stop="weekPointerDown(ci, ri, $event)"
                    v-on:pointerenter="weekPointerEnter(ci, ri)"
                    v-on:keydown.enter="editDefault(col.weekday, row.minute)"
                  >
                    <span class="ac-time"><i v-if="row.minute === -1" class="fa fa-asterisk ac-all-times"></i><template v-else>{{ row.label }}</template></span>
                    <i v-if="resolvedDefault(col.weekday, row.minute) && resolvedDefault(col.weekday, row.minute).cell.closed" class="fa fa-ban text-danger"></i>
                    <span class="ac-price"><i v-if="weekPrice(col.weekday, row.minute).perPerson" class="fa fa-user ac-person"></i>{{ weekPrice(col.weekday, row.minute).amount }}<span class="ac-cur" v-if="weekPrice(col.weekday, row.minute).currency">&nbsp;{{ currencyAddon }}</span><span v-if="weekPrice(col.weekday, row.minute).more" class="ac-more">+</span><sup v-if="resolvedDefault(col.weekday, row.minute) && hasCellRules(resolvedDefault(col.weekday, row.minute).cell)" class="ar-rules-mark">•</sup></span>
                  </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="small text-muted calendar-legend">
          <span><i class="fa fa-asterisk ac-all-times"></i> {{ _('All start times') }}</span>
          <span><i class="fa fa-user ac-person"></i> {{ _('Per person') }}</span>
          <span><span class="ac-legend-swatch ac-own"></span> {{ _('Set here') }}</span>
          <span><span class="ac-legend-swatch ac-inherited"></span> {{ _('From "Every day" or "All times"') }}</span>
          <span><span class="ac-legend-swatch ac-noprice"></span> {{ _('No price') }}</span>
          <span><span class="ac-legend-swatch ac-closed"></span> <i class="fa fa-ban text-danger"></i> {{ _('Closed') }}</span>
          <span>• {{ _('Included participants or participant limits') }}</span>
        </p>
      </template>

      <template #day="{ date }">
        <div v-if="days[date] && !days[date].length" class="ar-empty-day" :title="_('No start times')">–</div>
        <div
          v-for="slot in days[date] || []"
          :key="slot.start_minute"
          :class="'ac-slot ac-clickable ' + slotClass(slot)"
          :title="slotTitle(slot)"
          tabindex="0"
          v-on:pointerdown="pendingStart = parseInt(slot.start_minute, 10)"
          v-on:keydown.enter.stop.prevent="editSlot(date, slot.start_minute)"
        >
          <span class="ac-time">{{ formatMinute(slot.start_minute) }}</span>
          <i v-if="slot.closed" class="fa fa-ban text-danger"></i>
          <span :class="{ 'ac-price': true, 'derived-rate': slot.source === 'derived' }"><i v-if="slotPrice(slot).perPerson" class="fa fa-user ac-person"></i>{{ slotPrice(slot).amount }}<span class="ac-cur" v-if="slotPrice(slot).currency">&nbsp;{{ currencyAddon }}</span><span v-if="slotPrice(slot).more" class="ac-more">+</span></span>
        </div>
      </template>

      <template #legend>
        <p class="small text-muted calendar-legend">
          <span><span class="ac-legend-swatch ac-default"></span> {{ _('Weekly price') }}</span>
          <span><span class="ac-legend-swatch ac-override"></span> <span class="legend-dot"></span> {{ _('Set for the date') }}</span>
          <span v-if="isSelectedDerived"><span class="ac-legend-swatch ac-default"></span> <i>12 €</i> {{ _('Calculated from the followed rate') }}</span>
          <span><span class="ac-legend-swatch ac-noprice"></span> {{ _('No price') }}</span>
          <span><span class="ac-legend-swatch ac-closed"></span> <i class="fa fa-ban text-danger"></i> {{ _('Closed') }}</span>
        </p>
      </template>

      <template #form>
        <div class="pcs-inline-field" v-if="durations.length > 1">
          <label>{{ _('Durations') }}</label>
          <div class="pcs-toggles" role="group">
            <button
              v-for="minutes in durations"
              :key="'fd-' + minutes"
              type="button"
              :class="'btn btn-sm ' + (formDurations.includes(minutes) ? 'btn-success' : 'btn-default')"
              :aria-pressed="formDurations.includes(minutes) ? 'true' : 'false'"
              v-on:click="toggleFormDuration(minutes)"
            >{{ formatDuration(minutes) }}</button>
          </div>
        </div>
        <div class="pcs-inline-field" v-if="startOptions.length">
          <label :title="_('With every start time selected, the change applies to the whole day.')">{{ _('Start times') }}</label>
          <div class="pcs-toggles" role="group">
            <button
              v-for="minute in startOptions"
              :key="'so-' + minute"
              type="button"
              :class="'btn btn-sm ' + (isStartSelected(minute) ? 'btn-success' : 'btn-default')"
              :aria-pressed="isStartSelected(minute) ? 'true' : 'false'"
              v-on:click="toggleStart(minute)"
            >{{ formatMinute(minute) }}</button>
            <button type="button" class="btn btn-sm btn-link" v-if="excludedStarts.length" v-on:click="excludedStarts = []">{{ _('All') }}</button>
          </div>
        </div>
        <p class="pcs-form-note" v-if="selection.loading"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading the current prices…') }}</p>
        <p class="pcs-form-note" v-else-if="followsWeeklyOnly"><i class="fa fa-fw fa-info-circle"></i> {{ _('These dates follow the weekly prices. What you change here applies to these dates only.') }}</p>
        <ActivityRateFields
          :form="form"
          :tiers="tierList"
          :currency="currencyAddon"
          :placeholder="fieldPlaceholder"
          :closedEmptyLabel="selectEmptyLabel('closed')"
        />
      </template>

      <template #actions>
        <button type="button" class="btn btn-danger" :disabled="applying || !canRemoveChanges" :title="canRemoveChanges ? _('Return the selected times to the weekly prices') : _('The selected dates have no changes')" v-on:click="applyRange(true)">
          <i class="fa fa-fw fa-eraser"></i> {{ _('Remove date changes') }}
        </button>
        <button type="button" class="btn btn-success" :disabled="applying" v-on:click="applyRange(false)">
          <i class="fa fa-fw fa-check"></i> {{ _('Apply to selected dates') }}
        </button>
      </template>
    </PricingCalendarShell>
    <PricingDialog ref="weekDialog" :title="weeklyTitle" v-on:closed="weeklyClosed">
      <div class="pcs-inline-field">
        <label>{{ _('Weekdays') }}</label>
        <div class="pcs-toggles" role="group">
          <button
            v-for="col in weekColumns"
            :key="'wwd-' + col.weekday"
            type="button"
            :class="'btn btn-sm ' + (weekly.days.includes(col.weekday) ? 'btn-success' : 'btn-default')"
            :aria-pressed="weekly.days.includes(col.weekday) ? 'true' : 'false'"
            v-on:click="toggleWeekly('days', col.weekday)"
          >{{ col.label }}</button>
        </div>
      </div>
      <div class="pcs-inline-field" v-if="durations.length > 1">
        <label>{{ _('Durations') }}</label>
        <div class="pcs-toggles" role="group">
          <button
            v-for="minutes in durations"
            :key="'wdu-' + minutes"
            type="button"
            :class="'btn btn-sm ' + (weekly.durations.includes(minutes) ? 'btn-success' : 'btn-default')"
            :aria-pressed="weekly.durations.includes(minutes) ? 'true' : 'false'"
            v-on:click="toggleWeekly('durations', minutes)"
          >{{ formatDuration(minutes) }}</button>
        </div>
      </div>
      <div class="pcs-inline-field">
        <label :title="_('All times applies to the start times that have no value of their own.')">{{ _('Start times') }}</label>
        <div class="pcs-toggles" role="group">
          <button
            v-for="row in weekRows"
            :key="'wst-' + row.minute"
            type="button"
            :class="'btn btn-sm ' + (weekly.minutes.includes(row.minute) ? 'btn-success' : 'btn-default')"
            :aria-pressed="weekly.minutes.includes(row.minute) ? 'true' : 'false'"
            v-on:click="toggleWeekly('minutes', row.minute)"
          >{{ row.minute === -1 ? _('All times') : row.label }}</button>
          <button type="button" class="btn btn-sm btn-link" v-if="weekly.minutes.length < weekRows.length" v-on:click="weekly.minutes = weekRows.map(r => r.minute)">{{ _('All') }}</button>
        </div>
      </div>
      <p class="pcs-form-note" v-if="weeklyNote"><i class="fa fa-fw fa-info-circle"></i> {{ weeklyNote }}</p>
      <ActivityRateFields
        :form="weekly.form"
        :tiers="tierList"
        :currency="currencyAddon"
        :placeholder="weeklyPlaceholder"
        :help="weeklyHelp"
        :closedEmptyLabel="weeklyClosedLabel"
      />
      <template #actions>
        <button type="button" class="btn btn-danger" v-if="weeklyCanClear" :title="_('Remove the prices set here, so they follow Every day and All times again')" v-on:click="clearWeekly">
          <i class="fa fa-fw fa-eraser"></i> {{ _('Clear') }}
        </button>
        <button type="button" class="btn btn-success" v-on:click="saveWeekly">
          <i class="fa fa-fw fa-check"></i> {{ _('Save') }}
        </button>
      </template>
    </PricingDialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import currencies from '~/lib/currencies'
import rateplanhelpers from '~/lib/rateplanhelpers'
import ActivityRateFields from '~/components/ActivityRateFields.vue'
import PricingCalendarShell from '~/components/PricingCalendarShell.vue'
import PricingDialog from '~/components/PricingDialog.vue'
import RateplanPicker from '~/components/RateplanPicker.vue'

const ALL_WEEKDAYS = 0
const ALL_STARTS = -1
const WEEKDAY_KEYS = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa']

// Activity pricing: prices of an activity calendar priced product. Rate plans, the weekly default
// grid (weekday x start time) and dated overrides of the selected plan.
export default {
  mixins: [errors, formats, modals, currencies, rateplanhelpers],
  components: { ActivityRateFields, PricingCalendarShell, PricingDialog, RateplanPicker },
  props: {
    product: { type: Object, required: true },
    settings: { type: Object, default: null },
    shopId: { type: [String, Number], default: '' },
    apiKey: { type: String, default: '' },
    baseDomain: { type: String, default: '' },
    canEditRates: { type: Boolean, default: false },
    pricetiers: { type: Array, default: () => [] }, // The price tiers (ticket types) the product uses
    starttimes: { type: Array, default: () => [] }, // Start minutes of the product's start time rules
    termsOptions: { type: [Array, Object], default: () => [] }
  },
  data () {
    return {
      plans: [],
      selectedPlan: null,
      defaults: [],
      days: {},
      month: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      loading: false,
      loadSeq: 0,
      rangeFrom: '',
      rangeTo: '',
      rangeDays: ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'],
      excludedStarts: [],
      view: 'weekly',
      durations: [], // Durations of the activity in minutes, shortest first (from activityrates.json)
      selectedDuration: null,
      formDurations: [],
      pendingStart: null, // A start time chip that opened the date dialog
      dialogActive: false,
      weekly: { days: [], minutes: [], durations: [], states: {}, form: this.emptyForm(), onClosed: null }, // The weekly dialog: its weekdays × start times, and durations
      weekSel: { anchor: null, current: null, dragging: false, open: false, columns: false }, // Weekly cells selected by dragging ([column, row]); columns: whole days from the headings
      selection: { loading: false, known: false, days: {}, seq: 0 }, // Current prices of the dialog's dates
      prefill: {}, // The values the dialog's fields started from; only fields that differ are saved
      applying: false,
      appliedHint: '',
      form: this.emptyForm()
    }
  },
  computed: {
    ...mapGetters(['locale', 'version', 'timeFormat']),
    canLoadRates () {
      return !!(this.canEditRates && this.product && this.product.id)
    },
    currencyAddon () {
      return this.currencySymbol(this.settings && this.settings.currency ? this.settings.currency : 'EUR')
    },
    tierList () {
      return (this.pricetiers || []).filter(t => t && t.id).map(t => ({ id: String(t.id), name: t.name || ('#' + t.id) }))
    },
    weeklyCells () { // The weekly dialog's cells in the grid's order
      const cells = []
      this.weekColumns.forEach(col => {
        if (this.weekly.days.includes(col.weekday)) {
          this.weekRows.forEach(row => {
            if (this.weekly.minutes.includes(row.minute)) {
              cells.push({ weekday: col.weekday, start_minute: row.minute })
            }
          })
        }
      })
      return cells
    },
    weeklyPrefillKnown () { // The prices are loaded for the shown duration only
      return this.durations.length <= 1 || (this.weekly.durations.length === 1 && this.weekly.durations[0] === this.selectedDuration)
    },
    weeklyWholeWeek () {
      return this.weeklyCells.length === 1 && this.weeklyCells[0].weekday === ALL_WEEKDAYS && this.weeklyCells[0].start_minute === ALL_STARTS
    },
    weeklyTitle () {
      const label = list => list.length === 1 ? list[0] : (list.length ? list[0] + '–' + list[list.length - 1] : '')
      const days = this.weekColumns.filter(col => this.weekly.days.includes(col.weekday)).map(col => col.label)
      const times = this.weekRows.filter(row => this.weekly.minutes.includes(row.minute)).map(row => row.minute === ALL_STARTS ? this._('All times') : row.label)
      if (times.length === this.weekRows.length && this.weeklyCells.length > 1) { // Whole days: the days only
        return days.join(', ')
      }
      return (days.length > 3 ? label(days) : days.join(', ')) + ' · ' + (times.length > 3 ? label(times) : times.join(', '))
    },
    weeklyNote () {
      if (this.weeklyWholeWeek) {
        return this._('Used on every weekday and start time that has no value of its own.')
      }
      if (this.weeklyCells.length > 1) {
        return this._('%s start times selected. The values are saved to all of them; fields you don\'t change stay as they are.').replace('%s', String(this.weeklyCells.length * Math.max(1, this.weekly.durations.length)))
      }
      return ''
    },
    weeklyCanClear () {
      return !this.weeklyPrefillKnown || this.weeklyCells.some(c => this.ownDefault(c.weekday, c.start_minute))
    },
    weeklyClosedLabel () { // The empty choice of For sale in the weekly dialog
      const state = this.weekly.states.closed
      if (state && state.varies) {
        return this._('Varies')
      }
      return this.weeklyWholeWeek ? this._('Not set') : this._('As "Every day" or "All times"')
    },
    tierPriority () { // Ticket type ids in the shop's priority order (the first one, usually adults, is the shown price)
      const all = this.settings && Array.isArray(this.settings.ownPricetier) ? this.settings.ownPricetier.slice() : []
      if (all.length) {
        return all.sort((a, b) => (parseInt(a.priority) || 0) - (parseInt(b.priority) || 0) || parseInt(a.id) - parseInt(b.id)).map(t => String(t.id))
      }
      return this.tierList.map(t => String(t.id))
    },
    hasFormTiers () {
      return Object.values(this.form.tiers).some(v => v !== '' && v !== null && v !== undefined)
    },
    startOptions () {
      const set = new Set()
      ;(this.starttimes || []).forEach(m => {
        const n = parseInt(m, 10)
        if (!isNaN(n) && n >= 0) {
          set.add(n)
        }
      })
      Object.values(this.days).forEach(slots => {
        ;(slots || []).forEach(slot => set.add(parseInt(slot.start_minute, 10)))
      })
      return Array.from(set).sort((a, b) => a - b)
    },
    weekRows () {
      return this.startOptions.map(m => ({ minute: m, label: this.formatMinute(m) })).concat([{ minute: ALL_STARTS, label: this._('All start times') }])
    },
    weekColumns () {
      return this.weekdayLabels.map((label, i) => ({ weekday: i + 1, label })).concat([{ weekday: ALL_WEEKDAYS, label: this._('Every day') }])
    },
    defaultsByKey () {
      const map = {}
      this.defaults.forEach(cell => {
        map[parseInt(cell.weekday, 10) + ':' + parseInt(cell.start_minute, 10)] = cell
      })
      return map
    },
    isSelectedDerived () {
      return !!(this.selectedPlan && this.selectedPlan.rate_mode === 'derived')
    },
    derivedPlanHint () {
      if (!this.isSelectedDerived) {
        return ''
      }
      const follows = this.planFollowsText(this.selectedPlan)
      return this._('This rate follows the prices of %s. Prices you set here replace the calculated prices. Calculated prices are shown in italic.').replace(/%s/, follows)
    },
    hasSelection () {
      return !!(this.rangeFrom && this.rangeTo)
    },
    selectionDates () { // The dialog's dates, with the weekday filter
      if (!this.hasSelection) {
        return []
      }
      const out = []
      const to = this.fromIso(this.rangeTo)
      for (let d = this.fromIso(this.rangeFrom); d <= to; d.setDate(d.getDate() + 1)) {
        if (this.rangeDays.length === 7 || this.rangeDays.includes(WEEKDAY_KEYS[d.getDay()])) {
          out.push(this.toIso(d))
        }
      }
      return out
    },
    prefillAvailable () { // Current values are known for the shown duration only
      return this.selection.known && (this.durations.length <= 1 || (this.formDurations.length === 1 && this.formDurations[0] === this.selectedDuration))
    },
    selectionSlots () {
      if (!this.prefillAvailable) {
        return []
      }
      const starts = this.selectedStarts()
      const slots = []
      this.selectionDates.forEach(date => {
        ;(this.selection.days[date] || []).forEach(slot => {
          if (starts.includes(parseInt(slot.start_minute, 10))) {
            slots.push(slot)
          }
        })
      })
      return slots
    },
    fieldStates () { // Per field: the common value, or the differing values
      const states = {}
      const keys = ['price', 'includes', 'minparticipants', 'maxparticipants', 'closed'].concat(this.tierList.map(t => 'tier:' + t.id))
      keys.forEach(key => {
        const values = Array.from(new Set(this.selectionSlots.map(slot => this.slotFieldValue(slot, key))))
        states[key] = { value: values.length === 1 ? values[0] : '', varies: values.length > 1, values }
      })
      return states
    },
    followsWeeklyOnly () {
      return !!(this.selectionSlots.length && this.selectionSlots.every(slot => slot.source !== 'override'))
    },
    canRemoveChanges () {
      return !this.prefillAvailable || this.selectionSlots.some(slot => slot.source === 'override')
    },
    weekdayLabels () {
      return [this._('Mon'), this._('Tue'), this._('Wed'), this._('Thu'), this._('Fri'), this._('Sat'), this._('Sun')]
    },
    views () {
      return [{ id: 'weekly', label: this._('Weekly prices') }, { id: 'calendar', label: this._('Calendar') }]
    },
    selectedPlanSummary () { // One line under the plan picker: what the plans table used to show
      const plan = this.selectedPlan
      if (!plan) {
        return ''
      }
      const parts = []
      const range = this.planRangeText(plan)
      if (range) {
        parts.push(range)
      }
      if (plan.rate_mode === 'derived') {
        parts.push(this._('Follows the prices of') + ' ' + this.planFollowsText(plan))
      }
      if (parseInt(plan.terms_id)) {
        parts.push(this._('Terms') + ': ' + this.rateplanTermsLabel(plan, this.termsOptions))
      }
      if (plan.allow_cancellation_request !== null && plan.allow_cancellation_request !== undefined && plan.allow_cancellation_request !== '') {
        parts.push(this._('Cancellation requests') + ': ' + this.rateplanCancellationText(plan))
      }
      return parts.join(' · ')
    }
  },
  watch: {
    product () { // The product is reloaded after a save, so start times and plans follow the saved state
      this.reload()
    },
    version () {
      this.reload()
    },
    canLoadRates (ready) {
      if (ready) {
        this.reload()
      }
    },
    pricetiers () {
      this.form = this.emptyForm()
    },
    month () {
      this.refreshRates()
    },
    rangeFrom () {
      this.selectionChanged()
    },
    rangeTo () {
      this.selectionChanged()
    },
    rangeDays () {
      if (this.dialogActive) {
        this.applyPrefill()
      }
    },
    excludedStarts () {
      if (this.dialogActive) {
        this.applyPrefill()
      }
    },
    formDurations () {
      if (this.dialogActive) {
        this.applyPrefill()
      }
    }
  },
  methods: {
    api (path) {
      const versionStr = this.version && this.version !== 'default' ? '&version=' + this.version : ''
      return this.baseDomain + this.shopId + path + (path.includes('?') ? '&' : '?') + 'apikey=' + this.apiKey + versionStr
    },
    ratesUrl (query) {
      return this.api('/products/' + this.product.id + '/rateplans/' + this.selectedPlan.id + '/activityrates.json' + (query || ''))
    },
    formatDuration (minutes) {
      const m = parseInt(minutes, 10)
      if (!m) {
        return ''
      }
      if (m % 60 === 0) {
        return m === 60 ? this._('1 hour') : this._('%s hours').replace('%s', String(m / 60))
      }
      return this._('%s min').replace('%s', String(m))
    },
    selectDuration (value) {
      const minutes = parseInt(value, 10)
      if (!minutes || minutes === this.selectedDuration) {
        return
      }
      this.selectedDuration = minutes
      this.formDurations = [minutes]
      this.defaults = []
      this.days = {}
      this.refreshRates()
    },
    toggleFormDuration (minutes) {
      if (this.formDurations.includes(minutes)) {
        this.formDurations = this.formDurations.filter(m => m !== minutes)
      } else {
        this.formDurations = this.formDurations.concat([minutes]).sort((a, b) => a - b)
      }
    },
    emptyForm () {
      const tiers = {}
      ;(this.pricetiers || []).forEach(t => {
        if (t && t.id) {
          tiers[String(t.id)] = ''
        }
      })
      return { price: '', tiers, includes: '', minparticipants: '', maxparticipants: '', closed: '' }
    },
    toIso (d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    fromIso (iso) {
      if (!iso) {
        return null
      }
      const parts = String(iso).split('-')
      if (parts.length < 3) {
        return null
      }
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10))
    },
    formatMinute (minute) {
      const m = parseInt(minute, 10)
      const dt = DateTime.fromObject({ hour: Math.floor(m / 60) % 24, minute: m % 60 })
      return dt.isValid ? dt.toFormat(this.timeFormat || 'H.mm') : String(minute)
    },
    formatRate (rate) {
      if (rate === null || rate === undefined || rate === '') {
        return ''
      }
      const n = Number(rate)
      if (isNaN(n)) {
        return ''
      }
      return n % 1 ? n.toFixed(2) : String(n)
    },
    parseDecimal (val) {
      if (val === '' || val === null || val === undefined) {
        return null
      }
      if (typeof val === 'number') {
        return val
      }
      const n = parseFloat(String(val).replace(',', '.'))
      return isNaN(n) ? null : n
    },
    parseCount (val) {
      if (val === '' || val === null || val === undefined) {
        return null
      }
      const n = parseInt(val, 10)
      return isNaN(n) ? null : Math.max(0, n)
    },
    isBlank (val) {
      return val === '' || val === null || val === undefined
    },
    tierName (id) {
      const tier = this.tierList.find(t => String(t.id) === String(id))
      return tier ? tier.name : '#' + id
    },
    tierEntries (tiers) {
      if (!tiers || typeof tiers !== 'object') {
        return []
      }
      return Object.keys(tiers).map(id => ({ id, price: tiers[id] }))
    },
    // --- Rate plans ---
    isSelected (plan) {
      return !!(this.selectedPlan && String(this.selectedPlan.id) === String(plan.id))
    },
    planOptions (plan) {
      if (!plan || !plan.options) {
        return []
      }
      try {
        const parsed = typeof plan.options === 'string' ? JSON.parse(plan.options) : plan.options
        return Array.isArray(parsed) ? parsed : Object.values(parsed || {})
      } catch (e) {
        return []
      }
    },
    derivedOption (plan) {
      const found = this.planOptions(plan).find(opt => opt && opt.derived_option)
      return found ? found.derived_option : null
    },
    derivedSummary (plan) {
      const opt = this.derivedOption(plan)
      if (!opt || !Number(opt.rate)) {
        return ''
      }
      const labels = {
        increase_by_amount: this._('increased by %s'),
        decrease_by_amount: this._('decreased by %s'),
        increase_by_percent: this._('increased by %s'),
        decrease_by_percent: this._('decreased by %s')
      }
      const label = labels[opt.modifier] || labels.increase_by_amount
      const value = this.formatRate(opt.rate) + (String(opt.modifier).indexOf('percent') !== -1 ? ' %' : ' ' + this.currencyAddon)
      return label.replace(/%s/, value)
    },
    planFollowsText (plan) {
      if (!plan || plan.rate_mode !== 'derived') {
        return this._('Own prices')
      }
      const parent = this.plans.find(p => String(p.id) === String(plan.parent_rateplan_id))
      const name = parent ? parent.title : '#' + plan.parent_rateplan_id
      const modifier = this.derivedSummary(plan)
      return modifier ? name + ', ' + modifier : name
    },
    normalizePlans (data) {
      if (Array.isArray(data)) {
        return data.slice()
      }
      if (!data || typeof data !== 'object') {
        return []
      }
      if (Array.isArray(data.data)) {
        return data.data.slice()
      }
      return Object.values(data).filter(item => item && typeof item === 'object' && item.id)
    },
    async loadPlans (selectId) {
      const response = await axios.get(this.api('/products/' + this.product.id + '/rateplans.json') + '&_=' + Date.now())
      this.plans = this.normalizePlans(response.data).sort((a, b) => (parseInt(a.sort) || 0) - (parseInt(b.sort) || 0) || parseInt(a.id) - parseInt(b.id))
      const wanted = selectId || (this.selectedPlan && this.selectedPlan.id)
      const found = wanted ? this.plans.find(p => String(p.id) === String(wanted)) : null
      this.selectedPlan = found || this.plans.find(p => parseInt(p.is_default)) || this.plans[0] || null
    },
    async ensureDefaultPlan () {
      if (this.plans.length) {
        return
      }
      await axios.post(this.api('/products/' + this.product.id + '/rateplans.json'), {
        title: this.product.name || this._('Standard rate'),
        version: this.version,
        is_default: 1
      })
      await this.loadPlans()
    },
    async reload () {
      if (!this.canLoadRates) {
        return
      }
      const seq = ++this.loadSeq
      this.loading = true
      try {
        await this.loadPlans()
        if (seq !== this.loadSeq) {
          return
        }
        if (!this.plans.length) {
          await this.ensureDefaultPlan()
        }
        await this.loadRates(seq)
      } catch (e) {
        this.showError(e, undefined, true)
      }
      if (seq === this.loadSeq) {
        this.loading = false
      }
    },
    async loadRates (seq) {
      if (!this.selectedPlan) {
        this.defaults = []
        this.days = {}
        return
      }
      const from = this.toIso(new Date(this.month.getFullYear(), this.month.getMonth(), 1))
      const to = this.toIso(new Date(this.month.getFullYear(), this.month.getMonth() + 1, 0))
      const planId = this.selectedPlan.id
      const duration = this.selectedDuration
      const response = await axios.get(this.ratesUrl('?from=' + from + '&to=' + to + (duration ? '&duration=' + duration : '')) + '&_=' + Date.now())
      if ((seq && seq !== this.loadSeq) || !this.selectedPlan || String(this.selectedPlan.id) !== String(planId) || this.selectedDuration !== duration) {
        return
      }
      const data = response.data || {}
      this.durations = (Array.isArray(data.durations) ? data.durations : Object.values(data.durations || {})).map(m => parseInt(m, 10)).filter(m => m > 0)
      const shown = parseInt(data.duration_minutes, 10) || null
      if (this.selectedDuration !== shown) { // The first load, or a duration that was removed from the product
        this.selectedDuration = shown
        this.formDurations = shown ? [shown] : []
      }
      this.defaults = Array.isArray(data.defaults) ? data.defaults : Object.values(data.defaults || {})
      this.days = data.days && typeof data.days === 'object' && !Array.isArray(data.days) ? data.days : {}
    },
    async refreshRates () {
      const seq = ++this.loadSeq
      this.loading = true
      try {
        await this.loadRates(seq)
      } catch (e) {
        this.showError(e, undefined, true)
      }
      if (seq === this.loadSeq) {
        this.loading = false
      }
    },
    selectPlanById (id) {
      const plan = this.plans.find(p => String(p.id) === String(id))
      if (plan) {
        this.selectPlan(plan)
      }
    },
    planRangeText (plan) { // Group size of a plan: "10+ participants", "1–9 participants"
      const min = parseInt(plan && plan.minparticipants) || 0
      const max = parseInt(plan && plan.maxparticipants) || 0
      if (!min && !max) {
        return ''
      }
      if (!max) {
        return this._('%s+ participants').replace('%s', String(min))
      }
      return this._('%s participants').replace('%s', (min || 1) + '–' + max)
    },
    planOptionLabel (plan) {
      const star = parseInt(plan.is_default) ? '★ ' : ''
      const range = this.planRangeText(plan)
      if (plan.rate_mode !== 'derived') {
        return star + plan.title + (range ? ' (' + range + ')' : '')
      }
      const parent = this.plans.find(p => String(p.id) === String(plan.parent_rateplan_id))
      const bits = [this._('from %s').replace(/%s/, parent ? parent.title : '#' + plan.parent_rateplan_id)]
      if (range) {
        bits.unshift(range)
      }
      const modifier = this.derivedSummary(plan)
      if (modifier) {
        bits.push(modifier)
      }
      return star + plan.title + ' (' + bits.join(', ') + ')'
    },
    selectPlan (plan) {
      if (this.isSelected(plan)) {
        return
      }
      this.selectedPlan = plan
      this.defaults = []
      this.days = {}
      this.refreshRates()
    },
    planFields (plan) {
      const derived = this.derivedOption(plan) || {}
      const isPercent = String(derived.modifier || '').indexOf('percent') !== -1
      const rate = derived.rate || derived.rate === 0 ? derived.rate : ''
      const others = this.plans.filter(p => !plan || String(p.id) !== String(plan.id))
      const fields = this.rateplanBaseFields(plan, this.termsOptions, !this.plans.length).concat([
        { type: 'info', text: this._('Group size: a rate can be for groups of a certain size, for example a group rate for 10 or more participants. The rate that fits the group is offered to the customer.') },
        { title: this._('Minimum participants'), type: 'number', name: 'minparticipants', defaultValue: plan && parseInt(plan.minparticipants) ? String(plan.minparticipants) : '', placeholder: this._('No limit') },
        { title: this._('Maximum participants'), type: 'number', name: 'maxparticipants', defaultValue: plan && parseInt(plan.maxparticipants) ? String(plan.maxparticipants) : '', placeholder: this._('No limit') }
      ])
      if (!others.length) {
        return fields
      }
      const parentId = plan && plan.parent_rateplan_id ? String(plan.parent_rateplan_id) : String(others[0].id)
      return fields.concat([
        {
          title: this._('Prices'),
          type: 'select',
          name: 'rate_mode',
          defaultValue: plan && plan.rate_mode === 'derived' ? 'derived' : 'manual',
          options: [
            { value: 'manual', name: this._('Own prices') },
            { value: 'derived', name: this._('Follows the prices of another rate') }
          ]
        },
        {
          title: this._('Follows the prices of'),
          type: 'select',
          name: 'parent_rateplan_id',
          defaultValue: parentId,
          condition: 'rate_mode',
          conditionValue: ['derived'],
          options: others.map(p => ({ value: String(p.id), name: p.title }))
        },
        {
          title: this._('Adjustment'),
          type: 'select',
          name: 'derived_modifier',
          defaultValue: derived.modifier || 'increase_by_amount',
          condition: 'rate_mode',
          conditionValue: ['derived'],
          options: [
            { value: 'increase_by_amount', name: this._('Increase by amount') },
            { value: 'decrease_by_amount', name: this._('Decrease by amount') },
            { value: 'increase_by_percent', name: this._('Increase by percent') },
            { value: 'decrease_by_percent', name: this._('Decrease by percent') }
          ]
        },
        {
          title: this._('Price difference'),
          type: 'number',
          lang: this.numberLocale,
          step: 'any',
          name: 'derived_amount',
          defaultValue: isPercent ? '' : rate,
          placeholder: '0',
          addon: this.currencyAddon,
          condition: 'rate_mode',
          conditionValue: ['derived'],
          condition2: 'derived_modifier',
          conditionValue2: ['increase_by_amount', 'decrease_by_amount']
        },
        {
          title: this._('Percent difference'),
          type: 'number',
          lang: this.numberLocale,
          step: 'any',
          name: 'derived_percent',
          defaultValue: isPercent ? rate : '',
          placeholder: '0',
          addon: '%',
          condition: 'rate_mode',
          conditionValue: ['derived'],
          condition2: 'derived_modifier',
          conditionValue2: ['increase_by_percent', 'decrease_by_percent']
        },
        {
          type: 'info',
          text: this._('The booking price and every ticket type price follow the other rate with the difference. Prices set for this rate replace the calculated ones.'),
          condition: 'rate_mode',
          conditionValue: ['derived']
        }
      ])
    },
    planPayload (data) {
      const payload = this.rateplanBasePayload(data)
      payload.minparticipants = this.parseCount(data.minparticipants) || null
      payload.maxparticipants = this.parseCount(data.maxparticipants) || null
      const derived = data.rate_mode === 'derived' && !!data.parent_rateplan_id
      payload.rate_mode = derived ? 'derived' : 'manual'
      if (derived) {
        const modifier = data.derived_modifier || 'increase_by_amount'
        payload.parent_rateplan_id = data.parent_rateplan_id
        payload.derived_modifier = modifier
        payload.derived_amount = this.parseDecimal(modifier.indexOf('percent') !== -1 ? data.derived_percent : data.derived_amount) || 0
      }
      return payload
    },
    addPlan () {
      this.showDialog({
        title: this._('Add rate'),
        buttons: [
          { title: this._('Close'), icon: 'close', handler: () => {} },
          {
            title: this._('Save'),
            class: 'success',
            icon: 'check',
            requiredFields: ['title'],
            handler: async (data) => {
              const payload = this.planPayload(data)
              payload.sort = this.plans.length
              try {
                const response = await axios.post(this.api('/products/' + this.product.id + '/rateplans.json'), payload)
                const created = response.data && response.data.id ? response.data : null
                await this.loadPlans(created && created.id)
                await this.refreshRates()
              } catch (e) {
                this.showError(e, undefined, true)
                await this.loadPlans()
              }
            }
          }
        ],
        fields: this.planFields()
      })
    },
    editPlan (plan) {
      this.showDialog({
        title: this._('Edit rate'),
        buttons: [
          { title: this._('Close'), icon: 'close', handler: () => {} },
          ...(this.plans.length > 1 ? [{ title: this._('Delete'), class: 'danger', icon: 'trash', handler: () => this.deletePlan(plan) }] : []),
          {
            title: this._('Save'),
            class: 'success',
            icon: 'check',
            requiredFields: ['title'],
            handler: async (data) => {
              const payload = this.planPayload(data)
              if (!payload.is_default && parseInt(plan.is_default)) { // One default is always kept, so it moves by setting another plan as the default
                delete payload.is_default
              }
              const prev = Object.assign({}, plan)
              Object.assign(plan, { title: payload.title, description: payload.description, terms_id: payload.terms_id, allow_cancellation_request: payload.allow_cancellation_request, rate_mode: payload.rate_mode, minparticipants: payload.minparticipants, maxparticipants: payload.maxparticipants })
              try {
                await axios.put(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'), payload)
              } catch (e) {
                Object.assign(plan, prev)
                this.showError(e, undefined, true)
              }
              await this.loadPlans(plan.id)
              await this.refreshRates()
            }
          }
        ],
        fields: this.planFields(plan)
      })
    },
    async setDefault (plan) {
      if (parseInt(plan.is_default)) {
        return
      }
      const prev = this.plans.map(p => p.is_default)
      this.plans.forEach(p => { p.is_default = String(p.id) === String(plan.id) ? 1 : 0 })
      try {
        await axios.put(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'), { is_default: 1 })
      } catch (e) {
        this.plans.forEach((p, i) => { p.is_default = prev[i] })
        this.showError(e, undefined, true)
      }
      await this.loadPlans()
    },
    async deletePlan (plan) {
      if (!confirm(this._('Delete the rate %s?').replace('%s', plan.title))) {
        return
      }
      try {
        await axios.delete(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'))
      } catch (e) {
        this.showError(e, undefined, true)
        return
      }
      if (this.isSelected(plan)) {
        this.selectedPlan = null
      }
      await this.loadPlans()
      await this.refreshRates()
    },
    // --- Weekly default grid ---
    ownDefault (weekday, minute) {
      return this.defaultsByKey[weekday + ':' + minute] || null
    },
    /**
     * A weekly cell as the backend prices it: merged field by field from (every day, every start) →
     * (weekday, every start) → (every day, start) → (weekday, start). skipOwn leaves the cell itself
     * out (what it inherits).
     */
    resolvedDefault (weekday, minute, skipOwn = false) {
      const keys = []
      ;[[ALL_WEEKDAYS, ALL_STARTS], [weekday, ALL_STARTS], [ALL_WEEKDAYS, minute], [weekday, minute]].forEach(key => {
        if (!keys.some(k => k[0] === key[0] && k[1] === key[1])) {
          keys.push(key)
        }
      })
      if (skipOwn) {
        keys.pop()
      }
      const merged = { price: null, tiers: {}, includes: null, minparticipants: null, maxparticipants: null, closed: false }
      const from = {}
      let found = false
      keys.forEach(key => {
        const cell = this.ownDefault(key[0], key[1])
        if (!cell) {
          return
        }
        found = true
        ;['price', 'includes', 'minparticipants', 'maxparticipants'].forEach(field => {
          if (!this.isBlank(cell[field])) {
            merged[field] = cell[field]
            from[field] = key
          }
        })
        if (cell.tiers && Object.keys(cell.tiers).length) {
          merged.tiers = cell.tiers
          from.tiers = key
        }
        if (cell.closed !== null && cell.closed !== undefined) {
          merged.closed = !!cell.closed
          from.closed = key
        }
      })
      if (!found) {
        return null
      }
      return { cell: merged, from, inherited: !this.ownDefault(weekday, minute) }
    },
    defaultKeyLabel (key) { // Where an inherited value comes from: "Every day", "All times" or both
      if (!key) {
        return ''
      }
      const parts = []
      if (key[0] === ALL_WEEKDAYS) {
        parts.push(this._('Every day'))
      } else {
        parts.push(this.weekdayLabels[key[0] - 1])
      }
      parts.push(key[1] === ALL_STARTS ? this._('All times') : this.formatMinute(key[1]))
      return parts.join(', ')
    },
    weekSlotClass (weekday, minute) { // A price set in the cell stands out, an inherited one is quiet
      const resolved = this.resolvedDefault(weekday, minute)
      if (!resolved) {
        return 'ac-noprice'
      }
      if (resolved.cell.closed) {
        return resolved.inherited ? 'ac-closed ac-inherited-closed' : 'ac-closed'
      }
      return resolved.inherited ? 'ac-inherited' : 'ac-own'
    },
    // --- Selecting weekly cells: a click edits one cell, dragging (or Shift-click) selects a block ---
    isWeekCellSelected (ci, ri) {
      const sel = this.weekSel
      if (sel.open) { // The dialog's weekdays and start times
        return this.weekly.days.includes(this.weekColumns[ci].weekday) && this.weekly.minutes.includes(this.weekRows[ri].minute)
      }
      if (!sel.anchor || !sel.current || !sel.dragging) {
        return false
      }
      return ci >= Math.min(sel.anchor[0], sel.current[0]) && ci <= Math.max(sel.anchor[0], sel.current[0]) &&
        ri >= Math.min(sel.anchor[1], sel.current[1]) && ri <= Math.max(sel.anchor[1], sel.current[1])
    },
    weekPointerDown (ci, ri, event) { // ri null: the heading or the empty part of a day, which select whole days
      if (event.button !== undefined && event.button !== 0) {
        return
      }
      if (event.pointerType === 'mouse') {
        event.preventDefault()
      }
      const columns = ri === null
      const last = this.weekRows.length - 1
      const extend = event.shiftKey && this.weekSel.anchor
      const anchor = extend ? this.weekSel.anchor : [ci, columns ? 0 : ri]
      this.weekSel = { anchor: columns ? [anchor[0], 0] : anchor, current: [ci, columns ? last : ri], dragging: true, open: false, columns }
      window.addEventListener('pointerup', this.weekPointerUp, { once: true })
      window.addEventListener('pointercancel', this.weekPointerCancel, { once: true })
    },
    weekPointerEnter (ci, ri) {
      if (!this.weekSel.dragging) {
        return
      }
      if (this.weekSel.columns) {
        this.weekSel.current = [ci, this.weekRows.length - 1]
      } else if (ri !== null) {
        this.weekSel.current = [ci, ri]
      }
    },
    weekPointerCancel () {
      window.removeEventListener('pointerup', this.weekPointerUp)
      this.weekSel = { anchor: null, current: null, dragging: false, open: false, columns: false }
    },
    weekPointerUp () {
      window.removeEventListener('pointercancel', this.weekPointerCancel)
      if (!this.weekSel.dragging) {
        return
      }
      const sel = this.weekSel
      const cells = []
      for (let ci = Math.min(sel.anchor[0], sel.current[0]); ci <= Math.max(sel.anchor[0], sel.current[0]); ci++) {
        for (let ri = Math.min(sel.anchor[1], sel.current[1]); ri <= Math.max(sel.anchor[1], sel.current[1]); ri++) {
          cells.push({ weekday: this.weekColumns[ci].weekday, start_minute: this.weekRows[ri].minute })
        }
      }
      this.weekSel = Object.assign({}, sel, { dragging: false, open: true })
      const closed = () => { this.weekSel = Object.assign({}, this.weekSel, { open: false }) }
      if (cells.length === 1) {
        this.editDefault(cells[0].weekday, cells[0].start_minute, closed)
      } else {
        this.editDefaults(cells, closed)
      }
    },
    /**
     * The dialog of one or several weekly cells, laid out as the date dialog: its weekdays, durations
     * and start times start from the selection in the grid and can be changed. The fields show the
     * prices the cells have now, including what they take from "Every day" or "All times" (marked
     * under the field); fields whose values differ say so. Only the fields that are changed are
     * saved, to every cell, so the rest keep following the less specific cells.
     */
    editDefaults (cells, onClosed) {
      if (!this.selectedPlan || !cells.length) {
        return
      }
      this.weekly = {
        days: Array.from(new Set(cells.map(c => c.weekday))),
        minutes: Array.from(new Set(cells.map(c => c.start_minute))),
        durations: this.selectedDuration ? [this.selectedDuration] : [],
        states: {},
        form: this.emptyForm(),
        onClosed
      }
      this.weeklyPrefill()
      this.$refs.weekDialog.open()
    },
    toggleWeekly (list, value) { // A weekday, start time or duration on or off; at least one stays on
      const current = this.weekly[list]
      if (current.includes(value)) {
        if (current.length > 1) {
          this.weekly[list] = current.filter(v => v !== value)
        }
      } else {
        this.weekly[list] = current.concat([value])
      }
      this.weeklyPrefill()
    },
    weeklyStates () {
      const cells = this.weeklyCells
      const keys = ['price', 'includes', 'minparticipants', 'maxparticipants', 'closed'].concat(this.tierList.map(t => 'tier:' + t.id))
      const states = {}
      if (!this.weeklyPrefillKnown) {
        keys.forEach(key => { states[key] = { value: '', varies: false, from: '', unknown: true } })
        return states
      }
      const resolved = cells.map(c => this.resolvedDefault(c.weekday, c.start_minute))
      const owns = cells.map(c => this.ownDefault(c.weekday, c.start_minute))
      const text = v => v === null || v === undefined || v === '' ? '' : String(v)
      const read = (cell, key) => {
        if (key.indexOf('tier:') === 0) {
          return cell.tiers ? cell.tiers[key.substring(5)] : ''
        }
        if (key === 'includes') {
          return parseInt(cell.includes) > 0 ? cell.includes : ''
        }
        if (key === 'closed') {
          return cell.closed === null || cell.closed === undefined ? '' : (cell.closed ? '1' : '0')
        }
        return cell[key]
      }
      keys.forEach(key => {
        const values = Array.from(new Set(resolved.map(r => r ? text(read(r.cell, key)) : '')))
        const ownSet = owns.some(own => own && text(read(own, key)) !== '')
        let from = ''
        if (cells.length === 1 && resolved[0] && values[0] !== '' && !ownSet) { // The shown value comes from a less specific cell
          from = this.defaultKeyLabel(resolved[0].from[key.indexOf('tier:') === 0 ? 'tiers' : key])
        }
        states[key] = { value: values.length === 1 ? values[0] : '', varies: values.length > 1, from }
      })
      return states
    },
    /**
     * Fill the weekly dialog's fields with the cells' values. A field the user has changed keeps
     * its value when the weekdays, start times or durations change.
     */
    weeklyPrefill () {
      const states = this.weeklyStates()
      Object.keys(states).forEach(key => {
        const before = this.weekly.states[key] ? this.weekly.states[key].value : ''
        if (this.weeklyValue(key) === before) {
          if (key.indexOf('tier:') === 0) {
            this.weekly.form.tiers[key.substring(5)] = states[key].value
          } else {
            this.weekly.form[key] = states[key].value
          }
        }
      })
      this.weekly.states = states
    },
    weeklyValue (key) {
      const value = key.indexOf('tier:') === 0 ? this.weekly.form.tiers[key.substring(5)] : this.weekly.form[key]
      return value === null || value === undefined ? '' : String(value)
    },
    weeklyPlaceholder (key) {
      const state = this.weekly.states[key]
      if (state && state.unknown) {
        return this._('Leave unchanged')
      }
      return state && state.varies ? this._('Varies') : this._('Not set')
    },
    weeklyHelp (key) {
      const state = this.weekly.states[key]
      return state && state.from ? this._('Taken from %s').replace('%s', state.from) : ''
    },
    weeklyChanged (key) {
      return this.weeklyValue(key) !== this.weekly.states[key].value
    },
    weeklyPayload (cell) { // The cells of every selected duration
      const durations = this.weekly.durations.length ? this.weekly.durations : [this.selectedDuration]
      return this.weeklyCells.flatMap(c => durations.map(d => Object.assign({ weekday: c.weekday, start_minute: c.start_minute, duration_minutes: d }, cell)))
    },
    saveWeekly () {
      const f = this.weekly.form
      const values = {}
      if (this.weeklyChanged('price')) {
        values.price = this.parseDecimal(f.price)
      }
      if (this.tierList.some(tier => this.weeklyChanged('tier:' + tier.id))) { // Ticket type prices replace each other
        values.tiers = {}
        this.tierList.forEach(tier => {
          const price = this.parseDecimal(f.tiers[tier.id])
          if (price !== null) {
            values.tiers[tier.id] = price
          }
        })
      }
      ;['includes', 'minparticipants', 'maxparticipants'].forEach(key => {
        if (this.weeklyChanged(key)) {
          values[key] = this.parseCount(f[key])
        }
      })
      if (this.weeklyChanged('closed')) {
        values.closed = f.closed === '1' ? 1 : (f.closed === '0' ? 0 : null)
      }
      const payload = this.weeklyPayload(values)
      this.$refs.weekDialog.close()
      if (Object.keys(values).length) {
        this.saveDefaults(payload)
      }
    },
    clearWeekly () {
      const payload = this.weeklyPayload({ clear: 1 })
      this.$refs.weekDialog.close()
      this.saveDefaults(payload)
    },
    weeklyClosed () {
      if (this.weekly.onClosed) {
        this.weekly.onClosed()
      }
      this.weekly.onClosed = null
    },
    weekColumnClass (weekday) {
      if (weekday === ALL_WEEKDAYS) {
        return 'every-col'
      }
      return weekday >= 6 ? 'ar-weekend' : ''
    },
    dayClass (date) { // A dot for dated changes, a red top line for a closed day
      const slots = this.days[date] || []
      const classes = []
      if (slots.some(slot => slot.source === 'override')) {
        classes.push('pmg-changed')
      }
      if (slots.length && slots.every(slot => slot.closed)) {
        classes.push('pmg-closed')
      }
      return classes.join(' ')
    },
    editSlot (date, minute) { // A start time of a day: the dialog opens with only that start time selected
      this.pendingStart = parseInt(minute, 10)
      this.$refs.shell.editDay(date)
    },
    dialogOpening () { // A press on a start time chip that wasn't dragged to other days edits only that start time
      this.form = this.emptyForm()
      this.prefill = {}
      const oneStart = this.pendingStart !== null && this.rangeFrom === this.rangeTo
      this.excludedStarts = oneStart ? this.startOptions.filter(m => m !== this.pendingStart) : []
      this.pendingStart = null
      if (this.selectedDuration) {
        this.formDurations = [this.selectedDuration]
      }
      this.dialogActive = true
      this.loadSelection()
    },
    selectionChanged () {
      if (!this.hasSelection) { // The dialog was closed
        this.dialogActive = false
        return
      }
      if (this.dialogActive) {
        this.loadSelection()
      }
    },
    /**
     * Current prices of the dialog's dates: from the shown month, or loaded in 62-day parts (the
     * API's limit) for up to a year. Longer ranges are not prefilled.
     */
    async loadSelection () {
      if (!this.selectedPlan || !this.hasSelection) {
        return
      }
      const seq = ++this.selection.seq
      const dates = this.selectionDates
      const span = Math.round((this.fromIso(this.rangeTo) - this.fromIso(this.rangeFrom)) / 86400000) + 1
      if (dates.every(date => this.days[date])) {
        const days = {}
        dates.forEach(date => { days[date] = this.days[date] })
        this.selection = { loading: false, known: true, days, seq }
        this.applyPrefill()
        return
      }
      if (span > 366) {
        this.selection = { loading: false, known: false, days: {}, seq }
        this.applyPrefill()
        return
      }
      this.selection = { loading: true, known: false, days: {}, seq }
      const days = {}
      try {
        for (let from = this.fromIso(this.rangeFrom); from <= this.fromIso(this.rangeTo); from.setDate(from.getDate() + 62)) {
          const to = new Date(Math.min(new Date(from.getFullYear(), from.getMonth(), from.getDate() + 61).getTime(), this.fromIso(this.rangeTo).getTime()))
          const duration = this.selectedDuration ? '&duration=' + this.selectedDuration : ''
          const response = await axios.get(this.ratesUrl('?from=' + this.toIso(from) + '&to=' + this.toIso(to) + duration) + '&_=' + Date.now())
          Object.assign(days, (response.data && response.data.days) || {})
        }
      } catch (e) {
        if (seq === this.selection.seq) {
          this.selection = { loading: false, known: false, days: {}, seq }
          this.applyPrefill()
        }
        return
      }
      if (seq === this.selection.seq) {
        this.selection = { loading: false, known: true, days, seq }
        this.applyPrefill()
      }
    },
    slotFieldValue (slot, key) { // A slot's value of a form field as the field shows it
      if (key === 'price') {
        return slot.price === null || slot.price === undefined ? '' : String(Number(slot.price))
      }
      if (key.indexOf('tier:') === 0) {
        const price = slot.tiers ? slot.tiers[key.substring(5)] : undefined
        return price === null || price === undefined || price === '' ? '' : String(Number(price))
      }
      if (key === 'closed') {
        return slot.closed ? '1' : '0'
      }
      if (key === 'includes') {
        return parseInt(slot.includes) > 0 ? String(parseInt(slot.includes)) : ''
      }
      return slot[key] === null || slot[key] === undefined || slot[key] === '' ? '' : String(parseInt(slot[key], 10))
    },
    formValue (key) {
      const value = key.indexOf('tier:') === 0 ? this.form.tiers[key.substring(5)] : this.form[key]
      return value === null || value === undefined ? '' : String(value)
    },
    setFormValue (key, value) {
      if (key.indexOf('tier:') === 0) {
        this.form.tiers[key.substring(5)] = value
      } else {
        this.form[key] = value
      }
    },
    /**
     * Fill the fields with the dates' common values. A field the user has changed keeps its value.
     */
    applyPrefill () {
      const prefill = {}
      Object.keys(this.fieldStates).forEach(key => {
        const state = this.fieldStates[key]
        const value = this.prefillAvailable && !state.varies ? state.value : ''
        if (this.formValue(key) === (this.prefill[key] || '')) {
          this.setFormValue(key, value)
        }
        prefill[key] = value
      })
      this.prefill = prefill
    },
    formChanged (key) {
      return this.formValue(key) !== (this.prefill[key] || '')
    },
    fieldPlaceholder (key) {
      const state = this.fieldStates[key]
      if (state && state.varies) {
        const numbers = state.values.filter(v => v !== '').map(Number)
        if (!numbers.length) {
          return this._('Varies')
        }
        const money = key === 'price' || key.indexOf('tier:') === 0
        const min = Math.min(...numbers)
        const max = Math.max(...numbers)
        const text = this.formatRate(min) + '–' + this.formatRate(max) + (money ? ' ' + this.currencyAddon : '')
        return this._('Varies: %s').replace('%s', state.values.includes('') ? text + ', ' + this._('not set') : text)
      }
      return this.prefillAvailable ? this._('Not set') : this._('Leave unchanged')
    },
    selectEmptyLabel (key) {
      const state = this.fieldStates[key]
      return state && state.varies ? this._('Varies') : this._('Leave unchanged')
    },
    weekCellTitle (weekday, minute) {
      const resolved = this.resolvedDefault(weekday, minute)
      if (!resolved) {
        return this._('No price')
      }
      const lines = this.cellLines(resolved.cell)
      if (resolved.cell.closed) {
        lines.push(this._('Closed'))
      }
      if (resolved.inherited) {
        lines.push(this._('Inherited from "Every day" or "All start times"'))
      }
      return lines.join('\n')
    },
    hasCellRules (cell) {
      return !!(parseInt(cell.includes) || parseInt(cell.minparticipants) || parseInt(cell.maxparticipants))
    },
    cellLines (cell) {
      const lines = []
      if (!this.isBlank(cell.price) && (Number(cell.price) || !this.tierEntries(cell.tiers).length)) {
        lines.push(this.money(cell.price) + ' / ' + this._('booking'))
      }
      this.tierEntries(cell.tiers).forEach(t => {
        lines.push(this.tierName(t.id) + ' ' + this.money(t.price))
      })
      if (parseInt(cell.includes)) {
        lines.push(this._('incl. %s').replace(/%s/, cell.includes))
      }
      const min = parseInt(cell.minparticipants)
      const max = parseInt(cell.maxparticipants)
      if (min || max) {
        lines.push(this._('Participants') + ' ' + (min || 0) + '–' + (max || '∞'))
      }
      return lines
    },
    editDefault (weekday, minute, onClosed) {
      this.editDefaults([{ weekday, start_minute: minute }], onClosed)
    },
    async saveDefaults (cells) {
      if (!this.selectedPlan) {
        return
      }
      const prev = this.defaults.slice()
      cells.filter(cell => !cell.duration_minutes || cell.duration_minutes === this.selectedDuration).forEach(cell => { // Optimistic update of the shown duration
        const key = cell.weekday + ':' + cell.start_minute
        const rest = this.defaults.filter(d => parseInt(d.weekday, 10) + ':' + parseInt(d.start_minute, 10) !== key)
        const existing = this.defaults.find(d => parseInt(d.weekday, 10) + ':' + parseInt(d.start_minute, 10) === key) || {} // Fields left out stay as they are
        const merged = Object.assign({}, existing, cell)
        this.defaults = cell.clear ? rest : rest.concat([Object.assign(merged, { closed: merged.closed === null || merged.closed === undefined ? null : !!merged.closed })])
      })
      try {
        await axios.post(this.ratesUrl(), { defaults: cells.map(cell => Object.assign({ duration_minutes: this.selectedDuration }, cell)) })
        this.flashHint()
      } catch (e) {
        this.defaults = prev
        this.showError(e, undefined, true)
      }
      await this.refreshRates()
    },
    flashHint () {
      this.appliedHint = this._('Prices updated')
      setTimeout(() => { this.appliedHint = '' }, 2000)
    },
    // --- Month grid ---
    slotClass (slot) { // Dates that follow the weekly prices are quiet, dated changes stand out
      if (slot.closed) {
        return 'ac-closed'
      }
      if (slot.price === null || slot.price === undefined) {
        return 'ac-noprice'
      }
      if (slot.source === 'override') {
        return 'ac-override'
      }
      return slot.source === 'derived' ? 'ac-default ac-derived' : 'ac-default'
    },
    money (value) {
      return this.formatRate(value === null || value === undefined || value === '' ? 0 : value) + ' ' + this.currencyAddon
    },
    /**
     * Price of a chip without words: { amount, currency, perPerson (a ticket price), more (other
     * ticket types cost something else, or ticket prices come on top of the booking price) }. The
     * ticket price is the first ticket type's in priority order, as the listing price. The tooltip
     * lists every price.
     */
    slotPrice (slot) {
      const tiers = slot ? this.tierEntries(slot.tiers).filter(t => !isNaN(Number(t.price))) : []
      if (!slot || (this.isBlank(slot.price) && !tiers.length)) {
        return { amount: '–', currency: false, perPerson: false, more: false }
      }
      const booking = Number(slot.price) || 0
      if (!tiers.length || booking) {
        return { amount: this.formatRate(booking), currency: true, perPerson: false, more: tiers.length > 0 }
      }
      const first = this.tierPriority.map(id => tiers.find(t => String(t.id) === id)).find(t => t) || tiers[0]
      return { amount: this.formatRate(first.price), currency: true, perPerson: true, more: tiers.some(t => Number(t.price) !== Number(first.price)) }
    },
    weekPrice (weekday, minute) {
      const resolved = this.resolvedDefault(weekday, minute)
      return this.slotPrice(resolved ? resolved.cell : null)
    },
    slotTitle (slot) {
      const lines = [this.formatMinute(slot.start_minute)]
      if (slot.price === null || slot.price === undefined) {
        lines.push(this._('No price'))
      } else {
        lines.push(...this.cellLines(slot))
      }
      if (slot.closed) {
        lines.push(this._('Closed'))
      }
      const sources = {
        default: this._('Weekly price'),
        override: this._('Set for the date'),
        derived: this._('Calculated from the followed rate')
      }
      if (sources[slot.source]) {
        lines.push(sources[slot.source])
      }
      return lines.join('\n')
    },
    isStartSelected (minute) {
      return !this.excludedStarts.includes(minute)
    },
    toggleStart (minute) {
      if (this.excludedStarts.includes(minute)) {
        this.excludedStarts = this.excludedStarts.filter(m => m !== minute)
      } else {
        this.excludedStarts = this.excludedStarts.concat([minute])
      }
    },
    selectedStarts () {
      return this.startOptions.filter(m => !this.excludedStarts.includes(m))
    },
    async applyRange (clear) {
      if (!this.selectedPlan || !this.hasSelection) {
        return
      }
      const starts = this.selectedStarts()
      if (this.startOptions.length && !starts.length) {
        this.showError(this._('Select at least one start time.'))
        return
      }
      const allStarts = starts.length === this.startOptions.length
      if (this.durations.length > 1 && !this.formDurations.length) {
        this.showError(this._('Select at least one duration.'))
        return
      }
      const value = { date_from: this.rangeFrom, date_to: this.rangeTo }
      if (this.formDurations.length) {
        value.durations = this.formDurations.slice()
      }
      if (this.rangeDays.length && this.rangeDays.length < 7) {
        value.days = this.rangeDays.slice()
      }
      if (clear) {
        if (!confirm(this._('Remove the date changes of the selected dates?'))) {
          return
        }
        value.clear = 1
        value.start_minutes = allStarts ? [ALL_STARTS].concat(this.startOptions) : starts // Whole-day and single start time changes
      } else {
        if (!allStarts) {
          value.start_minutes = starts
        }
        // Only the fields the user changed: the others stay as they are (and keep following the weekly prices)
        const price = this.formChanged('price') ? this.parseDecimal(this.form.price) : null
        if (price !== null) {
          value.price = price
        }
        if (this.tierList.some(tier => this.formChanged('tier:' + tier.id))) { // Ticket type prices replace each other
          if (this.tierList.some(tier => this.isBlank(this.form.tiers[tier.id]) && this.fieldStates['tier:' + tier.id].varies)) {
            this.showError(this._('Enter a price for every ticket type. Ticket type prices replace each other.'))
            return
          }
          value.tiers = {}
          this.tierList.forEach(tier => {
            const tierPrice = this.parseDecimal(this.form.tiers[tier.id])
            if (tierPrice !== null) {
              value.tiers[tier.id] = tierPrice
            }
          })
        }
        ;['includes', 'minparticipants', 'maxparticipants'].forEach(field => {
          const n = this.formChanged(field) ? this.parseCount(this.form[field]) : null
          if (n !== null) {
            value[field] = n
          }
        })
        if (this.formChanged('closed') && (this.form.closed === '0' || this.form.closed === '1')) {
          value.closed = parseInt(this.form.closed, 10)
        }
        const changed = ['price', 'tiers', 'includes', 'minparticipants', 'maxparticipants', 'closed'].some(field => value[field] !== undefined)
        if (!changed) {
          this.showError(this._('Nothing was changed.'))
          return
        }
      }
      this.applying = true
      const prev = JSON.parse(JSON.stringify(this.days))
      if (!clear) {
        this.applyOptimistic(value)
      }
      try {
        await axios.post(this.ratesUrl(), { values: [value] })
        this.flashHint()
        this.form = this.emptyForm()
        if (this.$refs.shell) {
          this.$refs.shell.closePanel()
        }
      } catch (e) {
        this.days = prev
        this.showError(e, undefined, true)
      }
      this.applying = false
      await this.refreshRates()
    },
    applyOptimistic (value) {
      if (value.durations && !value.durations.includes(this.selectedDuration)) { // The calendar shows another duration
        return
      }
      const from = this.fromIso(value.date_from)
      const to = this.fromIso(value.date_to)
      for (let d = new Date(from.getTime()); d <= to; d.setDate(d.getDate() + 1)) {
        const iso = this.toIso(d)
        if (value.days && value.days.indexOf(WEEKDAY_KEYS[d.getDay()]) === -1) {
          continue
        }
        if (!this.days[iso]) {
          continue
        }
        this.days[iso] = this.days[iso].map(slot => {
          if (value.start_minutes && value.start_minutes.indexOf(parseInt(slot.start_minute, 10)) === -1) {
            return slot
          }
          const next = Object.assign({}, slot)
          ;['price', 'tiers', 'includes', 'minparticipants', 'maxparticipants'].forEach(field => {
            if (value[field] !== undefined) {
              next[field] = value[field]
            }
          })
          if (value.closed !== undefined) {
            next.closed = !!value.closed
          }
          next.source = 'override'
          return next
        })
      }
    }
  },
  beforeUnmount () {
    window.removeEventListener('pointerup', this.weekPointerUp)
    window.removeEventListener('pointercancel', this.weekPointerCancel)
  },
  mounted () {
    this.reload()
  }
}
</script>

<style scoped>
/* The weekly view is drawn like one week of the calendar */
.ar-week-grid {
  margin-bottom: 0;
  table-layout: fixed;
  background: #fff
}
.ar-week-grid th {
  font-weight: 600;
  color: #555
}
.ar-week-grid td.ar-week-day {
  min-width: 64px;
  height: 84px;
  vertical-align: top;
  padding: 4px
}
.ar-week-grid .ar-weekend {
  background-color: #fafafa
}
.ar-week-grid .every-col {
  background-color: #f3f6f9
}
.ar-week-label {
  display: none
}
/* Weekly cells being selected or edited together */
.ar-week-grid th.ar-week-head {
  cursor: pointer;
  user-select: none
}
.ar-week-grid th.ar-week-head:hover {
  background-color: #eef3f8
}
.ar-rules-mark {
  margin-left: 2px;
  color: #31708f
}
.derived-plan-hint,
.applied-hint {
  margin-top: 0;
  margin-bottom: 8px
}
.ar-empty-day {
  font-size: 12px;
  color: #999
}
.ar-duration-field {
  display: flex;
  align-items: center;
  margin: 0
}
.ar-duration-field label {
  margin: 0 8px 0 0
}
.ar-duration-field .form-control {
  width: auto
}
/* Narrow: the week as a list of weekdays, start times side by side (the month grid does the same) */
@container pricing-calendar (max-width: 680px) {
  .ar-week-grid,
  .ar-week-grid tbody,
  .ar-week-grid tr,
  .ar-week-grid td {
    display: block;
    width: 100%
  }
  .ar-week-grid {
    border: 0
  }
  .ar-week-grid thead {
    display: none
  }
  .ar-week-grid > tbody > tr > td.ar-week-day {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    height: auto;
    padding: 8px 6px;
    border-width: 0 0 1px 0
  }
  .ar-week-label {
    display: block;
    flex: 0 0 64px;
    font-weight: 600;
    line-height: 26px
  }
  .ar-week-slots {
    display: flex;
    flex-wrap: wrap;
    gap: 4px
  }
}
</style>
